import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Award, Send, XCircle } from "lucide-react";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { QuizQuestions, AttemptNotes } from "@/components/LsoParts";
import { lsoTrack, qs } from "@/lib/lso";

export default function LsoFinalExam({ kind = "staff" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const as = kind === "rental" ? "me" : params.get("as") || "me";
  const track = lsoTrack(kind, as);
  const hub = `${track.hubPath}${qs(as)}`;
  const [course, setCourse] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    track.getCourse().then((c) => { setCourse(c); setAnswers(new Array(c.final_exam.length).fill(-1)); }).catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, as]);

  const submit = async () => {
    if (answers.some((a) => a < 0)) return setErr("Please answer every question before submitting.");
    setBusy(true); setErr(null);
    try { setResult(await track.submitFinal(answers)); window.scrollTo(0, 0); }
    catch (e) { setErr(e.detail?.cooldown_until ? `Max attempts reached. Try again after ${new Date(e.detail.cooldown_until).toLocaleString()}.` : e.message); }
    finally { setBusy(false); }
  };

  if (!course) return <PortalPage title="Final Examination" back={hub} testId="lso-final-page">{err ? <ErrorNote>{err}</ErrorNote> : <Spinner />}</PortalPage>;
  const pass = course.pass_threshold_percent;

  return (
    <PortalPage eyebrow={`${course.final_exam.length} QUESTIONS • ${pass}% TO PASS`} title="Final Examination" back={hub} backLabel="Course hub" testId="lso-final-page">
      {result ? (
        <Card className="flex flex-col items-center p-8 text-center" data-testid="lso-final-result">
          {result.passed ? <Award size={64} className="text-[var(--accent)]" /> : <XCircle size={64} className="text-[#FF3333]" />}
          <p className="mt-3 font-display text-3xl font-bold uppercase">{result.passed ? "You passed — certificate issued" : "Not Quite There"}</p>
          <p className={`my-2 font-display text-6xl font-extrabold ${result.passed ? "text-[var(--accent)]" : "text-[#FF3333]"}`} data-testid="lso-final-score">{Math.round(result.score)}%</p>
          <p className="mb-3 text-sm text-neutral-400">{result.correct} of {result.total} correct • Best {Math.round(result.best_score)}%</p>
          <AttemptNotes r={result} />
          <div className="mt-4 w-full space-y-2">
            {result.certificate && <Btn onClick={() => navigate(`${track.hubPath}/certificate${qs(as)}`)} data-testid="lso-final-view-cert"><Award size={16} /> View Certificate</Btn>}
            {!result.passed && !result.in_cooldown && <Btn onClick={() => { setResult(null); setAnswers(new Array(course.final_exam.length).fill(-1)); }} data-testid="lso-final-retry">Retry Final Exam</Btn>}
            <Btn variant="outline" onClick={() => navigate(hub)} data-testid="lso-final-back">Back to Hub</Btn>
          </div>
        </Card>
      ) : (
        <>
          <QuizQuestions questions={course.final_exam} answers={answers} onChange={setAnswers} />
          <ErrorNote>{err}</ErrorNote>
          <Btn busy={busy} onClick={submit} data-testid="lso-final-submit"><Send size={14} /> Submit Final Exam</Btn>
          {busy && <p className="text-center text-xs text-neutral-500">Grading and issuing your certificate can take a few seconds. Your answers are safe.</p>}
        </>
      )}
    </PortalPage>
  );
}
